import {animate, style, transition, trigger} from "@angular/animations"

import {Component, computed, inject, OnInit, ChangeDetectionStrategy} from "@angular/core"
import {toSignal} from "@angular/core/rxjs-interop"
import {MatButtonModule} from "@angular/material/button"
import {MatIconModule} from "@angular/material/icon"
import {MatMenuModule} from "@angular/material/menu"
import {MatTableModule} from "@angular/material/table"
import {MatTooltipModule} from "@angular/material/tooltip"
import {JobsTableItemFragment} from "@app/platform/components/jobs/jobs-table/jobs-table.generated"
import {OverflowableTextComponent} from "@common/components/data"
import {ItemListComponent} from "@common/components/item/item-list/item-list.component"
import {ListInfoComponent} from "@common/components/item/list-info/list-info.component"
import {InfiniteListComponent} from "@common/components/lists/infinite-list/infinite-list.component"
import {CopyIdButtonComponent} from "@common/components/buttons/copy-id-button/copy-id-button.component"
import {TableCellComponent} from "@common/components/tables/table-cell/table-cell.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {jobPriorityIconClass} from "@common/helpers/jobs/icons"
import {formatForDisplay} from "@common/helpers/utils/dates"
import {TimeAgoPipe} from "@common/pipes/time-ago/time-ago.pipe"
import {BreakpointsService} from "@common/services/breakpoints/breakpoints.service"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {SortOrderService} from "@platform/services/data/sort-order/sort-order.service"
import {ContentTypeModel, JobOrderByInput, JobState, MutationUpdateJobInput} from "@generated"
import {JobThumbnailComponent} from "@platform/components/jobs/job-thumbnail/job-thumbnail.component"
import {
    JobsTableCancelJobGQL,
    JobsTableItemsGQL,
    JobsTableNudgeJobGQL,
    JobsTableResetJobPriorityGQL,
    JobsTableRestartJobGQL,
    JobsTableUpdateJobGQL,
} from "@platform/components/jobs/jobs-table/jobs-table.generated"
import {UserThumbnailComponent} from "@platform/components/users/user-thumbnail/user-thumbnail.component"

// What the list's "high priority" button assigns. Everything above 0 sorts ahead of default work;
// 50 leaves headroom on either side for priorities set by hand.
const HIGH_PRIORITY = 50

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-jobs-table",
    imports: [
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatTableModule,
        MatTooltipModule,
        JobThumbnailComponent,
        OverflowableTextComponent,
        TableCellComponent,
        CopyIdButtonComponent,
        InfiniteListComponent,
        ListInfoComponent,
        UserThumbnailComponent,
        TimeAgoPipe,
    ],
    templateUrl: "./jobs-table.component.html",
    styleUrl: "./jobs-table.component.scss",
    animations: [
        trigger("fadeInPlaceholder", [
            transition("void => *", [
                style({
                    opacity: 0.4,
                    scale: 0.7,
                }),
                animate("1000ms", style({opacity: 1, scale: 0.98})),
            ]),
        ]),
    ],
})
export class JobsTableComponent extends ItemListComponent<JobsTableItemFragment, MutationUpdateJobInput> implements OnInit {
    readonly breakpoints = inject(BreakpointsService)
    readonly can = inject(PermissionsService)
    readonly sortOrder = inject(SortOrderService)

    readonly itemsGql = inject(JobsTableItemsGQL)
    readonly updateJobGql = inject(JobsTableUpdateJobGQL)
    readonly cancelJobGql = inject(JobsTableCancelJobGQL)
    readonly nudgeJobGql = inject(JobsTableNudgeJobGQL)
    readonly restartJobGql = inject(JobsTableRestartJobGQL)
    readonly resetJobPriorityGql = inject(JobsTableResetJobPriorityGQL)

    // OVERLOADS
    protected override _contentTypeModel = ContentTypeModel.Job

    readonly $columns = computed(() => {
        if (this.breakpoints.$isDesktopLarge()) {
            return ["id", "state", "name", "priority", "action", "message", "creator"]
        } else if (this.breakpoints.$isDesktop()) {
            return ["id", "state", "name", "priority", "action", "message"]
        } else {
            return ["id", "state", "name", "priority", "action"]
        }
    })

    private readonly $jobFilter = toSignal(this.filters.jobFilter$, {initialValue: {}})
    private readonly $jobOrderBy = toSignal(this.sortOrder.jobs$, {initialValue: [] as JobOrderByInput[]})

    protected _fetchList = ({skip, take}: {skip: number; take: number}) =>
        fetchThrowingErrors(this.itemsGql)({
            take: take,
            skip: skip,
            filter: this.filters.jobFilter(),
            orderBy: this.$jobOrderBy(),
        }).then(({items, totalCount}) => ({items: items?.filter(Boolean) || [], totalCount: totalCount || 0}))

    protected _refreshItem = ({id}: {id?: string}) =>
        fetchThrowingErrors(this.itemsGql)({
            take: 1,
            filter: {...this.$jobFilter(), id: id ? {equals: id} : undefined},
            orderBy: this.$jobOrderBy(),
        }).then(({items}) => items?.[0] || undefined)

    protected readonly JobState = JobState
    protected readonly jobPriorityIconClass = jobPriorityIconClass

    async togglePriority(item: {id: string}, highPriority: boolean) {
        if (highPriority) {
            await mutateThrowingErrors(this.updateJobGql)({input: {id: item.id, priority: HIGH_PRIORITY}})
            await mutateThrowingErrors(this.nudgeJobGql)({id: item.id})
        } else {
            // Back to the default this job would have been created with — its creator's, else their
            // organization's. Writing a literal 0 here would promote a job whose creator is
            // deliberately deprioritized, silently undoing the penalty.
            await mutateThrowingErrors(this.resetJobPriorityGql)({id: item.id})
        }
        this.refreshItem(item)
    }

    async cancelJob(job: {id: string}) {
        await mutateThrowingErrors(this.cancelJobGql)({id: job.id})
        this.refreshItem(job)
    }

    async restartJob(job: {id: string}) {
        await mutateThrowingErrors(this.restartJobGql)({id: job.id})
        this.refreshItem(job)
    }

    rowClicked(job?: {id: string} | null) {
        if (job?.id) {
            this.router.navigate(["/jobs", job?.id], {queryParamsHandling: "preserve"})
        }
    }

    override ngOnInit() {
        super.ngOnInit()
    }

    protected readonly formatForDisplay = formatForDisplay
}
